import {animate, group, query, style, transition, trigger} from '@angular/animations';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {Sidebar} from './component/shared/sidebar/sidebar';
import {AlertWrapperComponent} from '@innovactions/frontend-library';
import {Company} from './service/company';
import {ColorSupporter} from './supporter/color-supporter';
import {LogoCache} from './service/logo-cache';
import {SelectSectorService} from './service/sector-selection';
import {Role} from './enum/role';
import {CurrentUserService} from './service/current-user';
import {LandingGateSupporter} from "./supporter/landing-gate-supporter";

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Sidebar, AlertWrapperComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  animations: [
    trigger('routeAnimations', [
      transition(
        (fromState, toState) =>
          fromState.startsWith('child-') && toState.startsWith('child-'),
        [
          style({position: 'relative', display: 'block', overflow: 'hidden'}),
          query(':enter, :leave', [
            style({
              position: 'absolute',
              inset: 0,
              width: '100%',
            })
          ], {optional: true}),
          query(':enter', [
            style({opacity: 0, transform: 'translateX(16px)'})
          ], {optional: true}),
          group([
            query(':enter', [
              animate('420ms cubic-bezier(0.8, -0.4, 0.5, 1)', style({opacity: 1, transform: 'translateX(0)'}))
            ], {optional: true}),
            query(':leave', [
              animate('210ms cubic-bezier(0.8, -0.4, 0.5, 1)', style({opacity: 0, transform: 'translateX(-16px)'}))
            ], {optional: true})
          ])
        ]
      ),
      transition('* <=> *', [
        style({position: 'relative', display: 'block', overflow: 'hidden'}),
        query(':enter, :leave', [
          style({
            position: 'absolute',
            inset: 0,
            width: '100%',
          })
        ], {optional: true}),
        query(':enter', [
          style({opacity: 0, transform: 'translateY(10px)'})
        ], {optional: true}),
        group([
          query(':enter', [
            animate('420ms cubic-bezier(0.8, -0.4, 0.5, 1)', style({opacity: 1, transform: 'translateY(0)'}))
          ], {optional: true}),
          query(':leave', [
            animate('210ms cubic-bezier(0.8, -0.4, 0.5, 1)', style({opacity: 0, transform: 'translateY(-8px)'}))
          ], {optional: true})
        ])
      ])
    ])
  ]
})
export class App implements OnInit, OnDestroy {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly router = inject(Router);

  constructor(private readonly sectorSelectionService: SelectSectorService,
              private readonly colorSupporter: ColorSupporter,
              private readonly logoCacheService: LogoCache,
              private readonly companyService: Company,
              private readonly landingGateSupporter: LandingGateSupporter) {}

  ngOnInit() {
    if (this.currentUserService.roles().includes(Role.SUPERADMIN)) {
      this.landingGateSupporter.markReady();
      return;
    }

    if (!localStorage.getItem("primary") || !localStorage.getItem("secondary")) {
      this.companyService.retrieveCompanyByUserId(["id", "sector { id }", "companySetting \n { \n primaryColor \n secondaryColor \n }"]).subscribe({
        next: (response) => {
          const primary = response?.companySetting?.primaryColor;
          const secondary = response?.companySetting?.secondaryColor;
          this.colorSupporter.setPrimaryColor(primary, false);
          this.colorSupporter.setSecondaryColor(secondary, false);
          this.sectorSelectionService.setSelectedSector(response.sector);
          this.sectorSelectionService.setSelectedCompany(response.id);
          this.landingGateSupporter.markReady();
        },
        error: () => {
          this.landingGateSupporter.markReady();
        },
      });
    } else {
      document.documentElement.style.setProperty('--color-primary', localStorage.getItem("primary"));
      document.documentElement.style.setProperty('--color-secondary', localStorage.getItem("secondary"));
      this.landingGateSupporter.markReady();
    }
  }

  ngOnDestroy() {
    this.colorSupporter.clear();
    this.logoCacheService.clear();
    this.sectorSelectionService.clear();
  }

  protected prepareRoute(outlet: RouterOutlet): string {
    if (!outlet || !outlet.isActivated) {
      return 'root:init';
    }

    const animationKey = outlet?.activatedRouteData?.['animation'];
    if (animationKey) {
      return animationKey;
    }

    const pathFromRoot = outlet?.activatedRoute?.snapshot?.pathFromRoot ?? [];
    const fullPath = pathFromRoot
      .map((route) => route.url.map((segment) => segment.path).join('/'))
      .filter(Boolean)
      .join('/');

    const depth = pathFromRoot.filter((route) => route.url.length).length;
    const isChildRoute = depth > 2;

    return `${isChildRoute ? 'child' : 'root'}-${fullPath || 'root'}`;
  }
}
